'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import type { YouTubePlayerInstance } from '@/app/lib/hooks/useYouTubeIframeApi'

/** Sobrescribe, inline, el tamaño que la propia API de YouTube pone en el
 * `<iframe>` que genera — un estilo inline siempre gana a cualquier regla de
 * `VideoCard.module.scss`. Mismo criterio que `HeroCarousel` (ver
 * `HeroCarousel/hooks/useHeroCarouselPlayback.ts`), pero con las
 * proporciones invertidas: un short de YouTube es vertical (9:16), no
 * panorámico (16:9), así que se sobredimensiona por altura en vez de por
 * anchura para cubrir la tarjeta activa sin distorsionar el vídeo —
 * `overflow: hidden` en el contenedor recorta el excedente (requiere
 * `container-type: size` ahí). */
function applyCoverIframeStyle(container: HTMLElement): void {
  const iframe = container.querySelector('iframe')
  if (!iframe) return
  iframe.style.position = 'absolute'
  iframe.style.top = '50%'
  iframe.style.left = '50%'
  iframe.style.width = '110cqw' // 100cqw × 1.1 de margen
  iframe.style.height = '195.556cqw' // 177.778cqw (100cqw · 16/9) × 1.1
  iframe.style.minWidth = '61.875cqh' // 56.25cqh (100cqh · 9/16) × 1.1
  iframe.style.minHeight = '110cqh'
  iframe.style.transform = 'translate(-50%, -50%)'
  iframe.style.border = '0'
}

interface UseActiveVideoPlayerOptions {
  /** Solo se monta un reproductor mientras es `true`; al pasar a `false` se
   * destruye (FR-014/015). */
  isActive: boolean
  /** `true` cuando `window.YT.Player` ya está disponible — cargado una sola
   * vez para todo el carrusel por `VideoClient`, no por cada tarjeta. */
  apiReady: boolean
  videoId: string
  /** Estado de silencio compartido por todo el carrusel (lo posee
   * `VideoClient`, no esta tarjeta): si el usuario activa el sonido en un
   * vídeo, el siguiente que se reproduzca también arranca con sonido, y
   * viceversa — por eso no es un estado local del reproductor. */
  muted: boolean
  /** Se dispara cuando YouTube reporta un error de reproducción — el
   * consumidor descarta el ítem (FR-017). */
  onError?: () => void
  /** Se dispara cuando el vídeo activo termina — el consumidor avanza al
   * siguiente, igual que el avance automático del carrusel de referencia
   * (`EventosDestacados`), pero disparado por el final real del vídeo en
   * vez de un temporizador fijo (ese proyecto no reproduce vídeo real). */
  onEnded?: () => void
  /** `false` mientras el carrusel está fuera del viewport: el vídeo se pausa
   * (o, si aún no había arrancado, no arranca) y se reanuda solo al volver a
   * la vista — salvo que el propio usuario lo hubiera pausado. */
  isInView?: boolean
}

interface UseActiveVideoPlayerResult {
  /** Ref del contenedor DOM estable donde se monta el reproductor — NUNCA se
   * le pasa directamente a `YT.Player` (ver comentario en `useHeroCarouselPlayback`
   * de `HeroCarousel`): YouTube sustituye el nodo que recibe, así que aquí se
   * cuelga dentro un `<div>` "sacrificable" desechable. */
  containerRef: (element: HTMLDivElement | null) => void
  isPlaying: boolean
  /** `true` desde que YouTube reporta el primer `PLAYING` real hasta que el
   * reproductor se desmonta — mientras es `false`, el `<iframe>` ya está
   * montado pero puede no estar pintando el vídeo todavía (buffering, fondo
   * propio de YouTube), así que la miniatura debe seguir tapándolo para no
   * dejar ver ese hueco. No vuelve a `false` al pausar (solo al desactivar
   * la tarjeta o cambiar de vídeo), para no volver a tapar un vídeo que ya
   * había arrancado. */
  isReady: boolean
  togglePlay: () => void
}

/**
 * Monta/destruye un único `YT.Player` para la tarjeta activa del carrusel
 * `Video`, con controles reales de reproducir/pausar y silenciar/activar
 * sonido (fieles al par de iconos de la tarjeta activa en el carrusel de
 * referencia, `EventosDestacados`, aquí funcionales de verdad — ese
 * proyecto es solo de diseño). Arranca en silencio de forma automática
 * (política de autoplay de los navegadores); el usuario puede pausar/
 * reanudar en cualquier momento, y activar/desactivar el sonido vía `muted`
 * (controlado por `VideoClient`, compartido por todo el carrusel).
 */
export function useActiveVideoPlayer({
  isActive,
  apiReady,
  videoId,
  muted,
  onError,
  onEnded,
  isInView = true,
}: UseActiveVideoPlayerOptions): UseActiveVideoPlayerResult {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)

  const containerElementRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<YouTubePlayerInstance | null>(null)
  const mountPointRef = useRef<HTMLDivElement | null>(null)
  const onErrorRef = useRef(onError)
  const onEndedRef = useRef(onEnded)
  // Leído solo dentro de `onReady` (asíncrono respecto al montaje) — un ref,
  // no una dependencia del efecto de montaje, para no destruir y recrear el
  // reproductor cada vez que el usuario cambia el sonido global.
  const mutedRef = useRef(muted)
  // Se sincronizan en un efecto (no durante el render) y se declara antes que
  // el resto para que ya estén al día cuando estos corran en el mismo commit.
  useEffect(() => {
    onErrorRef.current = onError
    onEndedRef.current = onEnded
    mutedRef.current = muted
  }, [onError, onEnded, muted])
  // `playerRef.current` se asigna en cuanto se construye el `YT.Player`
  // (síncrono), pero sus métodos (`mute`/`unMute`…) no están operativos
  // hasta que el propio `onReady` confirma que el `<iframe>` ya está
  // inicializado — llamarlos antes (p. ej. desde el efecto de sonido en
  // caliente, más abajo, si `muted` cambia en ese hueco) revienta con un
  // error real de la API de YouTube (no reproducible con el player de test,
  // que sí expone los métodos desde el primer instante).
  const playerReadyRef = useRef(false)
  // Leído en `onReady` (asíncrono) y en el efecto de visibilidad — mismo
  // motivo que `mutedRef`: no recrear el reproductor al entrar/salir de vista.
  const isInViewRef = useRef(isInView)
  // `true` si el usuario pausó con el control — salir y volver a la vista no
  // debe reanudar un vídeo que él mismo paró.
  const userPausedRef = useRef(false)
  // `true` si quien detuvo el vídeo fue la salida del viewport (o nunca llegó
  // a arrancar por estar fuera) — solo entonces se reanuda al volver.
  const pausedByViewRef = useRef(false)

  const containerRef = useCallback((element: HTMLDivElement | null) => {
    containerElementRef.current = element
  }, [])

  useEffect(() => {
    if (!isActive || !apiReady) return
    const container = containerElementRef.current
    const YT = window.YT
    if (!container || !YT) return

    const mountPoint = document.createElement('div')
    mountPoint.style.width = '100%'
    mountPoint.style.height = '100%'
    container.appendChild(mountPoint)
    mountPointRef.current = mountPoint

    const player = new YT.Player(mountPoint, {
      videoId,
      // `mute: 1` en `playerVars` (belt-and-braces junto al `player.mute()`/
      // `unMute()` de `onReady`, según el sonido global vigente): el
      // autoplay muted es lo único compatible con las políticas de los
      // navegadores si el sonido global está desactivado — mismo criterio
      // que `HeroCarousel`.
      playerVars: {
        autoplay: 0,
        controls: 0,
        mute: 1,
        playsinline: 1,
        modestbranding: 1,
        rel: 0,
        fs: 0,
        disablekb: 1,
        iv_load_policy: 3,
        cc_load_policy: 0,
      },
      events: {
        // Arranca SIEMPRE silenciado: es la única reproducción automática
        // que los navegadores garantizan sin excepción, tanto al navegar
        // manualmente (flecha/swipe/clic en una tarjeta que asoma) como al
        // avanzar solo al terminar el vídeo anterior (`onEnded`, sin ningún
        // gesto de usuario en ese instante). Activar el sonido `unMute()`
        // ANTES de reproducir (según el sonido global vigente) hacía que la
        // política de autoplay del navegador bloqueara en silencio el
        // `playVideo()` en cuanto el sonido global estaba activado — la
        // tarjeta activa se quedaba parada sin arrancar. Si el sonido global
        // está activado, se activa en `onStateChange` en cuanto se confirma
        // que el vídeo ya está reproduciéndose (alternar el mute de un vídeo
        // YA en marcha no está sujeto a esa misma restricción).
        onReady: () => {
          playerReadyRef.current = true
          player.mute()
          applyCoverIframeStyle(container)
          if (isInViewRef.current) {
            player.playVideo()
          } else {
            pausedByViewRef.current = true
          }
        },
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.PLAYING) {
            setIsPlaying(true)
            setIsReady(true)
            if (!mutedRef.current) player.unMute()
          } else if (event.data === YT.PlayerState.PAUSED) {
            setIsPlaying(false)
          } else if (event.data === YT.PlayerState.ENDED) {
            setIsPlaying(false)
            onEndedRef.current?.()
          }
        },
        onError: () => {
          onErrorRef.current?.()
        },
      },
    })
    playerRef.current = player

    return () => {
      player.destroy()
      mountPoint.remove() // defensivo: por si `destroy()` no limpia el nodo
      playerRef.current = null
      playerReadyRef.current = false
      userPausedRef.current = false
      pausedByViewRef.current = false
      mountPointRef.current = null
      setIsPlaying(false)
      setIsReady(false)
    }
  }, [isActive, apiReady, videoId])

  // Aplica en caliente el sonido global al reproductor activo cuando cambia
  // (el usuario lo activa/desactiva desde cualquier tarjeta) — no solo al
  // montar uno nuevo. Si el player aún no está listo, no hace falta actuar
  // aquí: `onReady`/`onStateChange` ya deciden el silencio inicial leyendo
  // `mutedRef.current` en su momento (ver comentario de más arriba).
  useEffect(() => {
    const player = playerRef.current
    if (!player || !playerReadyRef.current) return
    if (muted) player.mute()
    else player.unMute()
  }, [muted])

  // Pausa al salir del viewport y reanuda al volver, solo si la pausa la
  // provocó la salida (no el usuario). Si el reproductor aún no está listo,
  // `onReady` ya decide leyendo `isInViewRef`.
  useEffect(() => {
    isInViewRef.current = isInView
    const player = playerRef.current
    if (!player || !playerReadyRef.current) return
    if (!isInView) {
      if (userPausedRef.current || pausedByViewRef.current) return
      player.pauseVideo()
      pausedByViewRef.current = true
    } else if (pausedByViewRef.current) {
      pausedByViewRef.current = false
      player.playVideo()
    }
  }, [isInView])

  const togglePlay = useCallback(() => {
    const player = playerRef.current
    if (!player) return
    pausedByViewRef.current = false
    if (isPlaying) {
      userPausedRef.current = true
      player.pauseVideo()
    } else {
      userPausedRef.current = false
      player.playVideo()
    }
  }, [isPlaying])

  return { containerRef, isPlaying, isReady, togglePlay }
}
